import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { AuthActions } from '@actions/index'
import { Credentials, UserRolesModel, UserModel, RequestModel, ResellerModel } from '@models/index';
import * as fromAuth from '@reducers/index'
import { Observable } from 'rxjs';
import { NgxPermissionsService } from 'ngx-permissions';
import { LocationBusinessMetadata } from '@actions/auth/auth.actions';

/**
 * AuthFacadeService dispatches all needed actions
 * related to app authentication and gets all the app storage 
 * information asociated with the respective selectors.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthFacadeService {

  /**
   * AuthFacadeService Constructor
   * @param store App storage
   * @param permissionsService Service to load logged user permissions
   */
  constructor(
    private store: Store<any>,
    private permissionsService: NgxPermissionsService,
  ) { }

  /**
   * Gets the errors associated with 
   * the authenticatin flow.
   * @returns An observable of a string
   * that contains auth error.
   */
  getError$(): Observable<string> {
    return this.store.select(fromAuth.getErrorAuth)
  }

  /**
   * Gets the data to know if user
   * password changed successfully or 
   * not.
   * @returns A boolean observable. If true
   * then the password was changed successfully,
   * otherwise the password didn't change.
   */
  getPswChanged$(): Observable<boolean> {
    return this.store.select(fromAuth.getPswChanged)
  }

  /**
   * Gets the current seleted reseller
   * to associate the user with the white
   * label app.
   * @returns An observable that contains
   * the selected reseller data.
   */
  getSelectedReseller$(): Observable<ResellerModel> {
    return this.store.select(fromAuth.getSelectedReseller)
  }

  /**
   * Gets the reseller according to the 
   * browser url. Every reseller has a
   * different url.
   *  <pre>
     * <code class="language-typescript">
     * //Gets Get More Reviews Reseller
     * getReseller$(https://app.getmorereviews.com)
        </code>
     * </pre>
   * @param url The reseller app url
   * @returns The reseller associated
   * with the app url.
   */
  getReseller$(url: string) {
    url = url.includes("app.getmorereviews.com") ? url : url.replace('app.', '')
    url = !url.includes("//beta.") ? url : url.replace('//beta.', '//')

    const request: RequestModel = {
      params: `resellers/domain?url=${url}`,
    }
    this.store.dispatch(AuthActions.getReseller({ request }))
  }

  /**
   * Gets the metadata for which locations
   * the current user has access to, for
   * use in the navigation's search bar.
   */
  getLocationAutocompleteOptions$(): Observable<{ businessId: string, businessName: string, locations: LocationBusinessMetadata[] }[]> {
    return this.store.select(fromAuth.getLocationMetadata);
  }

  searchLocationAutocompleteOptions(resellerId: string = undefined) {
    this.store.dispatch(AuthActions.searchLocationAutocompleteOptions(resellerId ? { resellerId } : {}));
  }

  /**
   * Dispatches the action to log user
   * in.
   * <pre>
     * <code class="language-typescript">
     * //Logs in example@email.com user
     * login({
     *  username: 'example@email.com'
     *  password: 'pswexample'
     *  resellerId: '5e8388ba69254e3b7e931f3e'
     * })
        </code>
    * </pre>
   * @param credentials The user credentials.
   * Refer to { @link CredentialsModel }
   */
  login(credentials: Credentials) {
    this.store.dispatch(AuthActions.login({ credentials }))
  }

  /**
   * Dispatches the action to
   * sign up the user to the app
   * via AWS cognito.
   * <pre>
     * <code class="language-typescript">
     * signUp({
     *  email: 'example@email.com',
     *  password: 'pswexample',
     *  firstName: 'John',
     *  lastName: 'Doe',
     *  name: 'John',
     *  resellerId: '5e8388ba69254e3b7e931f3e',
     *  businessId: '5e8388ba69254e3b7e931f3d',
     *  id: '5e8388ba69254e3b7e931aaa',
     *  migrated: false,
     *  user: {
     *  createdAt: 1585678524539,
     *  email: "example@email.com",
     *  firstName: "John",
     *  id: "5e8388bc69254e3b7e933e27",
     *  lastName: "Doe",
     *  permissions: ["viewResellerList", "viewSiteAdminDashboard", "viewSiteAdminsMenu", "selectReseller"],
     *  role: {
     *    createdAt: 1571846464952,
     *    deleted: false,
     *    deletedAt: 1571846464952,
     *    id: "5db07941bade501e6f5bac5c",
     *    labelName: "Site admin",
     *    name: "SITE_ADMIN",
     *    shortName: "STAD",
     *    updatedAt: 1571846464952}
     *  }
     * })
        </code>
    * </pre>
   * @param payload Contains all sign up data
   */
  singUp(payload: UserModel) {
    this.store.dispatch(AuthActions.signUp({ payload }))
  }

  /**
   * Searches the data for the user to
   * sign up depending on the sign up
   * key.
   * <pre>
     * <code class="language-typescript">
     * getSignUpUser({
     *  key: 'https://app.getmorereviews.com/auth/sign-up/1f5412c7-fae58209-5e433469'
     * })
        </code>
    * </pre>
   * @param payload Contains the key data
   * that identifies the user configuration.
   */
  getSignUpUser(payload: { key: string }) {
    payload.key = payload.key.includes("app.getmorereviews.com") ? payload.key : payload.key.replace('app.', '')
    this.store.dispatch(AuthActions.getSignUpUser({ payload }))
  }

  /**
   * Dispatches account validation
   * for change pasword flow.
   * <pre>
     * <code class="language-typescript">
     * confirmSingUp({
     *  email: 'example@email.com',
     *  code: '644734'
     * })
        </code>
    * </pre>
   * @param payload Contains the data for password
   * recovery, where the code is given on an email
   * sent via cognito.
   */
  confirmSingUp(payload: { email: string, code: number }) {
    this.store.dispatch(AuthActions.validateAccount({ payload }))
  }

  /**
   * Selects current user profile on the
   * app state.
   * <pre>
   * <code class="language-typescript">
   * selectUserProfile({
   *  name: 'John',
   *  firstName: 'John',
   *  lastName: 'Doe',
   *  id: '5e8388ba69254e3b7e931aaa',
   *  email: 'example@email.com',
   *  password: 'pswexample',
   *  createdAt: 1585678524539,
   *  updatedAt: 1585678524539,
   *  lastLogin: 1585678524539,
   *  role: {
   *    createdAt: 1571846464952,
   *    deleted: false,
   *    deletedAt: 1571846464952,
   *    id: "5db07941bade501e6f5bac5c",
   *    labelName: "Site admin",
   *    name: "SITE_ADMIN",
   *    shortName: "STAD",
   *    updatedAt: 1571846464952}
   *  },
   *  permissions: ["viewResellerList", "viewSiteAdminDashboard", "viewSiteAdminsMenu", "selectReseller"],
   *  businessId: '5e8388ba69254e3b7e931f3d',
   *  resellerId: '5e8388ba69254e3b7e931f3e',
   *  reseller: {
   *    archived: false,
   *    availableLicenses: 10000,
   *    businesses: 0,
   *    createDate: 1585678522830,
   *    favIcon: "data:image/vnd.microsoft.icon;base64,eUEVf3lBEs95QRmveUEQ33lBEA95EU.../3A==",
   *    fromEmail: "",
   *    helpEmail: "",
   *    id: "5e8388ba69254e3b7e931f3e",
   *    logo: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAB/YA",
   *    name: "Get More Reviews",
   *    shortName: "",
   *    siteURL: "https://app.getmorereviews.com",
   *    updateDate: 1586548426110,
   *    usedLicenses: 1563
   *  },
   *  migrated: false,
   * })
      </code>
   * </pre>
   * @param user An object that stores the current logged
   * user information.
   */
  selectUserProfileByRole(user: UserModel) {
    this.permissionsService.loadPermissions(user.permissions)
    this.store.dispatch(AuthActions.loginSuccess({ user }))
  }

  /**
   * Dispatches the action for
   * sending the password recovery email
   * via AWS cognito.
   * <pre>
     * <code class="language-typescript">
     * //Logs in example@email.com user
     * sendEmailPasswordRecover(example@email.com)
        </code>
    * </pre>
   * @param email The user email to send cognito
   * password recovery email.
   */
  sendEmailPasswordRecover(email: string, resellerId: string) {
    this.store.dispatch(AuthActions.sendEmailPasswordRecover({ email,resellerId }))
  }

  /**
   * Dispatches action to change
   * the add the new password
   * to cognito. 
   * <pre>
     * <code class="language-typescript">
     * confirmNewPassword({
     *  username: 'example@email.com',
     *  code: '644734',
     *  newPassword: 'newpswexample',
     *  resellerId:'5e8388ba69254e3b7e931f3e'
     * })
        </code>
    * </pre>
   * @param payload Contains the data to
   * reset user pasword via cognito.
   */
  confirmNewPassword(payload: { username: string, code: string, newPassword: string, resellerId: string }) {
    this.store.dispatch(AuthActions.confirmNewPassword({ payload }))
  }

  /**
   * Dispatches the action to
   * change user password via cognito.
   * <pre>
     * <code class="language-typescript">
     * changePassword({
     *  psw: 'pswexample',
     *  newPsw: 'newpwexample',
     * })
        </code>
    * </pre>
   * @param payload Contains the required data
   * to change the user password.
   */
  changePassword(payload: { psw: string, newPsw: string }) {
    this.store.dispatch(AuthActions.changePassword({ payload }))
  }

  /**
   * Dispatches the action that
   * stores the current reseller
   * on the app state.
   * <pre>
     * <code class="language-typescript">
     * selectReseller({
     *  archived: false,
     *    availableLicenses: 10000,
     *    businesses: 0,
     *    createDate: 1585678522830,
     *    favIcon: "data:image/vnd.microsoft.icon;base64,eUEVf3lBEs95QRmveUEQ33lBEA95EU.../3A==",
     *    fromEmail: "",
     *    helpEmail: "",
     *    id: "5e8388ba69254e3b7e931f3e",
     *    logo: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAB/YA",
     *    name: "Get More Reviews",
     *    shortName: "",
     *    siteURL: "https://app.getmorereviews.com",
     *    updateDate: 1586548426110,
     *    usedLicenses: 1563
     * })
        </code>
    * </pre>
   * @param payload Contains the information
   * of the reseller to be selected.
   */
  selectReseller(payload: { id: string, name: string, shortName: string, logo: string, siteURL: string, favIcon: string }) {
    this.store.dispatch(AuthActions.selectReseller({ payload }))
  }

  /**
   * Dispatches the action that writes
   * to the app state wheter the password
   * was changed successfully or not.
   * <pre>
     * <code class="language-typescript">
     * //The password change was successfull
     * setPswChanged(true)
        </code>
    * </pre>
   * @param pswChanged Indicates if the
   * password was changed or not.
   */
  setPswChanged(pswChanged: boolean) {
    this.store.dispatch(AuthActions.setPswChanged({ pswChanged }))
  }

  /**
   * Dispacthes the action that
   * logs the user out via
   * cognito.
   * <pre>
     * <code class="language-typescript">
     * logOut()
        </code>
    * </pre>
   */
  logOut() {
    this.store.dispatch(AuthActions.logOut())
  }

  /**
   * Dispatches the actions to get
   * current logged user data.
   * <pre>
     * <code class="language-typescript">
     * getLoggedUserData(
     *  'example@email.com',
     *  '5e8388ba69254e3b7e931f3e'
     * )
        </code>
    * </pre>
   * @param email The email of the current logged user.
   * @param resellerId The reseller id to which the user belongs to.
   */
  getLoggedUserData(email: string, resellerId: string) {
    const request: RequestModel = {
      params: `users/login?email=${email}&resellerId=${resellerId}`,
    }
    this.store.dispatch(AuthActions.getLoggedUserData({ request }))
  }
}
